//Based on middleware-api from redux/real-world example

import { normalize } from 'normalizr';

import { Transmit } from '$Utils';

// Fetches an API response and normalizes the result JSON according to schema.
// This makes every API response have the same shape, regardless of how nested it was.
const fetchAPIAsync = (options, schema, metadata) => {
    return Transmit(options).then(
        response => {
            if (response.status == 204) {
                return new Promise((resolve, reject) => {
                    window.setTimeout(() => {
                        resolve(fetchAPIAsync(options, schema, metadata));
                    }, 2000);
                });
            }
            const loadTime = new Date() - metadata.startLoad;

            if (!schema) return response;

            return Object.assign({}, normalize(response, schema));
        },
        failureResponse => {
            const loadTime = new Date() - metadata.startLoad;
            const status = failureResponse.status;
            throw failureResponse; //re-throw to allow downstream catch
        }
    );
};

// Action key that carries API call info interpreted by this Redux middleware.
export const CALL_API_ASYNC = Symbol('Call API Async');

// A Redux middleware that interprets actions with CALL_API_ASYNC info specified.
// Performs the call and promises when such actions are dispatched.
export const apiAsyncMiddleware = store => next => action => {
    const callAPIAsync = action[CALL_API_ASYNC];
    if (typeof callAPIAsync === 'undefined') {
        return next(action);
    }
    let { endpoint, actionPayload } = callAPIAsync;

    const { schema, types, options } = callAPIAsync;
    let resultEndpoint = options.resultEndpoint;

    if (typeof endpoint === 'function') {
        endpoint = endpoint(store.getState());
    }
    if (typeof endpoint !== 'string') {
        throw new Error('Specify a string endpoint URL.');
    }
    if (!Array.isArray(types) || types.length !== 3) {
        throw new Error('Expected an array of three action types.');
    }
    if (!types.every(type => typeof type === 'string')) {
        throw new Error('Expected action types to be strings.');
    }

    if (!actionPayload) actionPayload = {};

    const actionWith = data => {
        const finalAction = Object.assign({}, action, data, actionPayload);
        delete finalAction[CALL_API_ASYNC];
        return finalAction;
    };

    //The `options` object is what get passed to the XHR call,
    //along with the `endpoint` property which is passed as `url`.
    //If you are POSTing data, nest the `data` object under `options`
    const transmitOptions = Object.assign({}, { url: endpoint }, options);
    const [requestType, successType, failureType] = types;

    //let Redux know the request has started
    next(actionWith({ type: requestType }));

    let metadata = {
        startLoad: new Date(),
        url: endpoint,
        type: options.type.toUpperCase()
    };
    return Transmit(transmitOptions)
        .then(response => {
            const taskID = response.task_id;

            if (!resultEndpoint) {
                resultEndpoint = '/api/v2/scheduling/async-result/';
            }

            const asyncOptions = {
                url: `${resultEndpoint}${taskID}/`,
                type: 'GET'
            };
            return fetchAPIAsync(asyncOptions, schema, metadata).then(
                response => {
                    return next(
                        actionWith({
                            response,
                            metadata,
                            type: successType
                        })
                    );
                },
                error => {
                    return next(
                        actionWith({
                            type: failureType,
                            metadata,
                            error: error
                        })
                    );
                }
            );
        })
        .catch(response => {
            return next(
                actionWith({
                    type: failureType,
                    error: response.statusText
                })
            );
        });
};
